<template>
  <dx-modal
    id="invoice-create-modal"
    :size="isEnabled ? 'lg' : 'md'"
    title="Emitir Nota Fiscal"
    :value="show"
    @input="close"
  >
    <template v-if="isEnabled">
      <div class="loading loading-lg my-2" v-if="loading"></div>
      <template v-else>
        <div v-if="statusReason" class="alert alert-error mb-2">
          <div class="text-pre">{{ statusReason }}</div>
        </div>
        <div class="columns">
          <div
            class="column col-12 form-group"
            :class="{'has-error': $v.form.entity.name.$error}"
          >
            <label class="form-label text-bold">Tomador de serviços</label>
            <dx-autocomplete
              id="patient-name"
              v-model="entityAc"
              :readonly="!!form.entity.id"
              :source="findEntity"
              label="name"
              track-by="id"
              @select="setEntity"
              :debounce="800"
              placeholder="Nome, CPF, CNPJ ou data de nascimento"
            >
              <template v-slot:action>
                <button
                  class="btn btn-action input-group-btn btn-icon"
                  @click="unsetEntity"
                  :class="entityAc ? 'btn-gray' : 'btn-neutral'"
                  tabindex="-1"
                >
                  <fa-icon :icon="['fal', entityAc ? 'times' : 'search']"/>
                </button>
              </template>
              <template v-slot="{ item }">
                <div>{{ item.name }}</div>
                <small class="text-primary">{{ item.summary }}</small>
              </template>
            </dx-autocomplete>
            <st-form-input-hint :validator="$v.form.entity.name"/>
          </div>
          <template v-if="hasEntity">
            <div
              class="column col-5 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.identity.value.$error}"
            >
              <label class="form-label" for="invoice-identity">Documento</label>
              <st-input-identity
                id="invoice-identity"
                :types="['cpf', 'cnpj']"
                v-model="form.entity.identity"
              />
              <st-form-input-hint :validator="$v.form.entity.identity.value"/>
            </div>
            <div
              class="column col-4 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.email.$error}"
            >
              <label class="form-label" for="invoice-email">Email</label>
              <input
                type="text"
                id="invoice-email"
                class="form-input"
                v-model="form.entity.email"
                placeholder="email@email.com"
              >
              <st-form-input-hint :validator="$v.form.entity.email">
                <template v-if="!$v.form.entity.email.email">E-mail inválido</template>
                <template v-else>Informe e-mail ou telefone</template>
              </st-form-input-hint>
            </div>
            <div
              class="column col-3 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.phone.$error}"
            >
              <label class="form-label" for="invoice-phone">Telefone</label>
              <input
                type="text"
                id="invoice-phone"
                class="form-input"
                v-model="form.entity.phone"
                v-mask-phone.br
                placeholder="(00) 0000-0000"
              >
              <st-form-input-hint :validator="$v.form.entity.phone">
                Informe e-mail ou telefone
              </st-form-input-hint>
            </div>

            <!-- ADDRESS - INI -->
            <div
              class="column col-3 col-md-5 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.address.postalCode.$error}"
            >
              <label for="address-postal-code" class="form-label">CEP</label>
              <div class="input-group">
                <input
                  type="text"
                  id="address-postal-code"
                  class="form-input"
                  v-model="form.entity.address.postalCode"
                  placeholder="00.000-000"
                  @change="loadAddressByPostalCode"
                  v-mask-cep
                  autocomplete="nope"
                >
                <button
                  type="button"
                  class="btn btn-neutral btn-icon btn-action input-group-btn"
                  :class="{loading: loadingAddress}"
                  :disabled="loadingAddress"
                  @click="loadAddressByPostalCode"
                >
                  <fa-icon :icon="['fal', 'search']"></fa-icon>
                </button>
              </div>
              <st-form-input-hint :validator="$v.form.entity.address.postalCode"/>
            </div>
            <div
              class="column col-6 col-md-12 form-group"
              :class="{'has-error': $v.form.entity.address.street.$error}"
            >
              <label for="address-addressLine1" class="form-label">Logradouro</label>
              <input
                type="text"
                id="address-addressLine1"
                class="form-input"
                v-model="form.entity.address.street"
                placeholder="Avenida Brasil"
                autocomplete="nope"
              >
              <st-form-input-hint :validator="$v.form.entity.address.street"/>
            </div>
            <div
              class="column col-3 col-md-12 form-group"
              :class="{'has-error': $v.form.entity.address.number.$error}"
            >
              <label for="address-number" class="form-label">Número</label>
              <input
                type="text"
                id="address-number"
                class="form-input"
                v-model="form.entity.address.number"
                placeholder="000"
                autocomplete="nope"
              >
              <st-form-input-hint :validator="$v.form.entity.address.number"/>
            </div>
            <div class="column col-3 col-md-6 col-sm-12 form-group">
              <label for="address-addressLine2" class="form-label">Complemento</label>
              <input
                type="text"
                id="address-addressLine2"
                class="form-input"
                v-model="form.entity.address.complement"
                placeholder="casa, sobrado, loja..."
                autocomplete="nope"
              >
            </div>
            <div
              class="column col-3 col-md-6 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.address.neighborhood.$error}"
            >
              <label for="address-neighborhood" class="form-label">Bairro</label>
              <input
                type="text"
                id="address-neighborhood"
                placeholder="Bairro"
                v-model="form.entity.address.neighborhood"
                class="form-input"
                autocomplete="nope">
              <st-form-input-hint :validator="$v.form.entity.address.neighborhood"/>
            </div>
            <div
              class="column col-3 col-md-6 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.address.city.$error}"
            >
              <label for="address-city" class="form-label">Cidade</label>
              <input
                type="text"
                id="address-city"
                class="form-input"
                v-model="form.entity.address.city"
                placeholder="Cidade"
                autocomplete="nope"
              >
              <st-form-input-hint :validator="$v.form.entity.address.city"/>
            </div>
            <div
              class="column col-3 col-md-6 col-sm-12 form-group"
              :class="{'has-error': $v.form.entity.address.state.$error}"
            >
              <label for="address-state" class="form-label">Estado</label>
              <select
                id="address-state"
                class="form-select"
                v-model="form.entity.address.state"
              >
                <option value="">[Selecione]</option>
                <option
                  v-for="(text, value) in states"
                  :value="value"
                  :key="value"
                >{{ value }}
                </option>
              </select>
              <st-form-input-hint :validator="$v.form.entity.address.state"/>
            </div>
            <!-- ADDRESS - END -->
          </template>
        </div>

        <div v-if="!hasEntity" class="empty py-2 mt-2">
          <div class="empty-icon mt-2">
            <fa-icon :icon="['fal', 'user-plus']" size="2x"/>
          </div>
          <div class="empty-title h6">Selecione um tomador</div>
          <div class="empty-subtitle">
            Busque e selecione o tomador de serviços acima para começar a emissão da nota.
          </div>
        </div>

        <template v-if="hasEntity">
          <div class="divider" style="margin-top: .6rem; margin-bottom: .6rem;"/>

          <div class="columns">
            <div
              class="column col-3 col-md-6 col-sm-12 form-group"
              :class="{'has-error': $v.form.totalAmount.$error}"
            >
              <label class="form-label text-bold">Valor da nota fiscal</label>
              <div class="input-group">
                <span class="input-group-addon">R$</span>
                <dx-input-number
                  class="form-input"
                  maxlength="10"
                  :precision="2"
                  v-model="form.totalAmount"
                />
              </div>
              <st-form-input-hint :validator="$v.form.totalAmount"/>
            </div>
            <div class="column col-3 col-md-6 col-sm-12 form-group mb-2">
              <label class="form-label">Deduções</label>
              <div class="input-group">
                <span class="input-group-addon">R$</span>
                <dx-input-number
                  class="form-input"
                  v-model="form.deductions"
                  :precision="2"
                />
              </div>
            </div>
            <div class="column col-3 col-md-6 col-sm-12 form-group mb-2" v-if="isCompany">
              <label class="form-label">Tipo de retenção</label>
              <select class="form-select" v-model="retentionType" @change="applyRetentionType">
                <option value="none">Sem retenção</option>
                <option value="company_simples">PJ Simples Nacional (só IRRF)</option>
                <option value="company_federal">PJ Regime Normal (IRRF+PIS+COFINS+CSLL)</option>
                <option value="public">Órgão público (IRRF + ISS retido)</option>
              </select>
            </div>
            <div class="column col-3 col-md-6 col-sm-12 form-group mb-2" v-if="isCompany">
              <label class="form-label">Tipo de recolhimento do ISS</label>
              <select class="form-select" v-model="form.service.issWithheldAtSource">
                <option :value="true">ISS retido na fonte pelo tomador</option>
                <option :value="false">ISS recolhido pelo prestador</option>
              </select>
            </div>
            <div
              class="column col-4 col-md-6 col-sm-12 form-group mb-2"
              :class="{'has-error': $v.form.service.issRate.$error}"
            >
              <label class="form-label">Alíquota de ISS</label>
              <div class="input-group">
                <span class="input-group-addon">%</span>
                <dx-input-number
                  class="form-input"
                  v-model="form.service.issRate"
                  :precision="2"
                />
                <span class="input-group-addon">R$</span>
                <input
                  class="form-input text-right"
                  :value="formatNumber(form.service.issAmount)"
                  :readonly="true"
                  tabindex="-1"
                />
              </div>
              <st-form-input-hint :validator="$v.form.service.issRate"/>
            </div>
            <div class="column col-4 col-md-6 col-sm-12 form-group">
              <label class="form-label">CBS</label>
              <div class="input-group">
                <span class="input-group-addon">%</span>
                <dx-input-number
                  class="form-input"
                  v-model="form.service.cbsRate"
                  :precision="2"
                />
                <span class="input-group-addon">R$</span>
                <input
                  class="form-input text-right"
                  :value="formatNumber(form.service.cbsAmount)"
                  :readonly="true"
                  tabindex="-1"
                />
              </div>
            </div>
            <div class="column col-4 col-md-6 col-sm-12 form-group">
              <label class="form-label">IBS</label>
              <div class="input-group">
                <span class="input-group-addon">%</span>
                <dx-input-number
                  class="form-input"
                  v-model="form.service.ibsRate"
                  :precision="2"
                />
                <span class="input-group-addon">R$</span>
                <input
                  class="form-input text-right"
                  :value="formatNumber(form.service.ibsAmount)"
                  :readonly="true"
                  tabindex="-1"
                />
              </div>
            </div>
            <div class="column col-4 col-md-6 col-sm-12 form-group">
              <label class="form-label">Redução IBS/CBS</label>
              <div class="input-group">
                <span class="input-group-addon">%</span>
                <dx-input-number
                  class="form-input"
                  v-model="form.service.ibsCbsReduction"
                  :precision="2"
                />
              </div>
            </div>
            <template v-if="isCompany">
              <div class="column col-4 col-md-6 col-sm-12 form-group">
                <label class="form-label">IR</label>
                <div class="input-group">
                  <span class="input-group-addon">%</span>
                  <dx-input-number
                    class="form-input"
                    v-model="form.service.irRate"
                    :precision="2"
                  />
                  <span class="input-group-addon">R$</span>
                  <input
                    class="form-input text-right"
                    :value="formatNumber(form.service.irAmount)"
                    :readonly="true"
                    tabindex="-1"
                  />
                </div>
              </div>
              <div class="column col-4 col-md-6 col-sm-12 form-group">
                <label class="form-label">PIS</label>
                <div class="input-group">
                  <span class="input-group-addon">%</span>
                  <dx-input-number
                    class="form-input"
                    v-model="form.service.pisRate"
                    :precision="2"
                  />
                  <span class="input-group-addon">R$</span>
                  <input
                    class="form-input text-right"
                    :value="formatNumber(form.service.pisAmount)"
                    :readonly="true"
                    tabindex="-1"
                  />
                </div>
              </div>
              <div class="column col-4 col-md-6 col-sm-12 form-group">
                <label class="form-label">COFINS</label>
                <div class="input-group">
                  <span class="input-group-addon">%</span>
                  <dx-input-number
                    class="form-input"
                    v-model="form.service.cofinsRate"
                    :precision="2"
                  />
                  <span class="input-group-addon">R$</span>
                  <input
                    class="form-input text-right"
                    :value="formatNumber(form.service.cofinsAmount)"
                    :readonly="true"
                    tabindex="-1"
                  />
                </div>
              </div>
              <div class="column col-4 col-md-6 col-sm-12 form-group">
                <label class="form-label">CSLL</label>
                <div class="input-group">
                  <span class="input-group-addon">%</span>
                  <dx-input-number
                    class="form-input"
                    v-model="form.service.csllRate"
                    :precision="2"
                  />
                  <span class="input-group-addon">R$</span>
                  <input
                    class="form-input text-right"
                    :value="formatNumber(form.service.csllAmount)"
                    :readonly="true"
                    tabindex="-1"
                  />
                </div>
              </div>
              <div class="column col-4 col-md-6 col-sm-12 form-group">
                <label class="form-label">INSS</label>
                <div class="input-group">
                  <span class="input-group-addon">%</span>
                  <dx-input-number
                    class="form-input"
                    v-model="form.service.inssRate"
                    :precision="2"
                  />
                  <span class="input-group-addon">R$</span>
                  <input
                    class="form-input text-right"
                    :value="formatNumber(form.service.inssAmount)"
                    :readonly="true"
                    tabindex="-1"
                  />
                </div>
              </div>
            </template>
          </div>

          <div class="columns">
            <div
              class="column col-6 col-sm-12 form-group mb-2"
              :class="{'has-error': $v.form.service.description.$error}"
            >
              <label class="form-label">Descrição do serviço</label>
              <textarea
                class="form-input"
                rows="3"
                v-model="form.service.description"
              />
              <st-form-input-hint :validator="$v.form.service.description"/>
            </div>
            <div class="column col-6 col-sm-12 form-group">
              <label class="form-label">Observações</label>
              <textarea
                class="form-input"
                rows="3"
                v-model="form.observations"
              />
            </div>
          </div>

          <div v-if="isRetentionUnconfigured" class="toast toast-error">
            Alíquotas de retenção (IR/PIS/COFINS/CSLL) não configuradas em
            Configurações — a nota sairá sem retenção.
          </div>
          <div v-if="isRetentionSuppressed" class="toast">
            Total de retenção ≤ R$ 10,00 — não será destacado na nota.
          </div>
        </template>
      </template>
    </template>

    <div class="empty" v-else>
      <div class="empty-icon">
        <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
      </div>
      <div class="empty-title h6">Nota Fiscal Eletrônica</div>
      <div class="empty-subtitle">
        O serviço de emissão de Nota Fiscal Eletrônica não está habilitado para sua conta.
      </div>
      <div>
        Para ativá-lo, acesse
        <router-link to="/settings">Configurações Gerais</router-link>
        → <strong>Integrações</strong>.
      </div>
      <div class="empty-action">
        <router-link
          class="btn btn-info-outline"
          to="/settings"
        >Configurar Nota Fiscal
        </router-link>
      </div>
    </div>

    <template v-slot:footer>
      <template v-if="isEnabled && hasEntity">
        <button
          class="btn btn-primary mr-2"
          :class="{ loading: saving }"
          @click="save"
          :disabled="saving || loading"
        >{{ statusReason ? 'Tentar novamente' : 'Emitir Nota Fiscal' }}
        </button>
      </template>
      <button class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { round } from 'lodash';
import { mapState } from 'vuex';
import {
  required, requiredIf, minValue, email,
} from 'vuelidate/lib/validators';
import states from '@/data/states';
import { CNPJ, CPF } from '@/data/identity-types';
import cpfValidator from '@/data/validators/cpf';
import cnpjValidator from '@/data/validators/cnpj';
import cpf from '@/filters/cpf';
import cnpj from '@/filters/cnpj';
import { clearNonNumbers } from '@/helpers/string';
import formatNumber from '@/filters/number';
import { extractStreetAndNumber } from '@/helpers/address';
import { first } from '@/helpers/array';
import { buildOrigin } from '@/helpers/origin';
import { CREATE_FISCAL_DOCUMENT } from '@/data/actions/modules/financial';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    entity: {
      type: Object,
    },
    source: {
      // Nota existente (ex.: cancelada) para reabrir a tela já preenchida.
      type: Object,
      default: null,
    },
    totalAmount: {
      type: Number,
      default: 0,
    },
    issRate: {
      type: Number,
      default: 0,
    },
    irRate: {
      type: Number,
      default: 0,
    },
    pisRate: {
      type: Number,
      default: 0,
    },
    cofinsRate: {
      type: Number,
      default: 0,
    },
    csllRate: {
      type: Number,
      default: 0,
    },
    inssRate: {
      type: Number,
      default: 0,
    },
    cbsRate: {
      type: Number,
      default: 0,
    },
    ibsRate: {
      type: Number,
      default: 0,
    },
    originType: {
      type: String,
    },
    originId: {
      type: String,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadingAddress: false,
      statusReason: null,
      sourceOrigin: null,
      form: this.blankForm(),
      retentionType: 'none',
      entityAc: null,
      identityTypes: [CPF, CNPJ],
      formatNumber,
      states,
    };
  },
  validations() {
    const identityValidator = this.form.entity.identity.type === CPF
      ? { cpf: cpfValidator }
      : { cnpj: cnpjValidator };

    return {
      form: {
        entity: {
          name: { required },
          email: {
            required: requiredIf(() => !this.form.entity.phone),
            email,
          },
          phone: {
            required: requiredIf(() => !this.form.entity.email),
          },
          identity: {
            value: {
              required,
              ...identityValidator,
            },
          },
          address: {
            postalCode: { required },
            street: { required },
            number: { required },
            neighborhood: { required },
            city: { required },
            state: { required },
          },
        },
        service: {
          description: { required },
          issRate: {
            required,
            minValue: minValue(0.01),
          },
        },
        totalAmount: {
          required,
          minValue: minValue(0.01),
        },
      },
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    ...mapState({
      branch: ({ auth }) => auth.user.branch,
    }),
    settings() {
      return this.branch.settings.fiscalDocument;
    },
    isEnabled() {
      return this.settings && this.settings.enabled;
    },
    canCreate() {
      return this.$can(CREATE_FISCAL_DOCUMENT);
    },
    origin() {
      return buildOrigin(this.originType, this.originId);
    },
    // Só mostra documento/contato/endereço depois que um tomador é escolhido.
    hasEntity() {
      return !!(this.form.entity && this.form.entity.name);
    },
    isCompany() {
      return this.form.entity.identity.type === CNPJ;
    },
    // Total das retenções na fonte (federais + ISS quando retido). O ISS não
    // retido aparece na nota mas não abate do líquido, então não entra na soma.
    retentionTotal() {
      const s = this.form.service;
      const iss = s.issWithheldAtSource ? (s.issAmount || 0) : 0;
      return (s.irAmount || 0) + (s.pisAmount || 0) + (s.cofinsAmount || 0)
        + (s.csllAmount || 0) + (s.inssAmount || 0) + iss;
    },
    // Regra da contabilidade: total de retenção <= R$ 10,00 não é destacado.
    isRetentionSuppressed() {
      return this.retentionTotal > 0 && this.retentionTotal <= 10;
    },
    // Escolheu um tipo de retenção PJ, mas as alíquotas federais (IR/PIS/COFINS/
    // CSLL) estão zeradas nas Configurações fiscais — a nota sairá sem retenção.
    isRetentionUnconfigured() {
      if (!['company_simples', 'company_federal'].includes(this.retentionType)) {
        return false;
      }
      const s = this.form.service;
      return (s.irRate || 0) + (s.pisRate || 0) + (s.cofinsRate || 0)
        + (s.csllRate || 0) === 0;
    },
  },
  watch: {
    isCompany(value) {
      // Pessoa física nunca retém imposto: zera ISS retido e retenções federais.
      if (!value) {
        this.form.service.issWithheldAtSource = false;
        ['ir', 'pis', 'cofins', 'csll', 'inss'].forEach((field) => {
          this.form.service[`${field}Rate`] = 0;
        });
      }
    },
  },
  methods: {
    async init() {
      this.loading = true;

      const entity = this.entity && this.entity.id
        ? await this.findEntityById(this.entity.id)
        : this.entity;

      this.setEntity(entity);
      this.form.totalAmount = this.totalAmount;

      this.setWatchers();

      if (this.settings) {
        if (!this.form.service.issRate) {
          this.form.service.issRate = this.settings.service.issRate;
        }
        this.form.service.nationalTaxationCode = this.settings.service.nationalTaxationCode;
        this.form.service.municipalCode = this.settings.service.municipalCode;
        this.form.service.nbsCode = this.settings.service.nbsCode;
        this.form.service.description = this.settings.service.description;
        if (!this.form.service.cbsRate) {
          this.form.service.cbsRate = this.settings.service.cbsRate || 0;
        }
        if (!this.form.service.ibsRate) {
          this.form.service.ibsRate = this.settings.service.ibsRate || 0;
        }
        if (!this.form.service.ibsCbsReduction) {
          this.form.service.ibsCbsReduction = this.settings.service.ibsCbsReduction || 0;
        }
        // Retenções federais NÃO são pré-carregadas (padrão = sem retenção).
        // Entram via o seletor "Tipo de retenção" (applyRetentionType).
      }

      if (this.source) {
        this.prefillFromSource(this.source);
      }

      this.loading = false;
    },
    applyRetentionType() {
      const s = (this.settings && this.settings.service) || {};
      const zeroFederal = () => {
        this.form.service.irRate = 0;
        this.form.service.pisRate = 0;
        this.form.service.cofinsRate = 0;
        this.form.service.csllRate = 0;
        this.form.service.inssRate = 0;
      };

      if (this.retentionType === 'public') {
        // Órgão público: retém IRRF na fonte + ISS retido; sem PIS/COFINS/CSLL.
        zeroFederal();
        this.form.service.irRate = s.irRate || 0;
        this.form.service.issWithheldAtSource = true;
      } else if (this.retentionType === 'company_simples') {
        // PJ Simples Nacional: retém só IRRF; ISS não retido.
        zeroFederal();
        this.form.service.irRate = s.irRate || 0;
        this.form.service.issWithheldAtSource = false;
      } else if (this.retentionType === 'company_federal') {
        // PJ Regime Normal: IRRF + PIS + COFINS + CSLL; ISS não retido.
        this.form.service.irRate = s.irRate || 0;
        this.form.service.pisRate = s.pisRate || 0;
        this.form.service.cofinsRate = s.cofinsRate || 0;
        this.form.service.csllRate = s.csllRate || 0;
        this.form.service.inssRate = 0;
        this.form.service.issWithheldAtSource = false;
      } else {
        // Sem retenção (padrão).
        zeroFederal();
        this.form.service.issWithheldAtSource = false;
      }
    },
    prefillFromSource(source) {
      this.setSourceEntity(source.entity);

      this.form.totalAmount = source.totalAmount || 0;
      this.form.deductions = source.deductions || 0;
      this.form.observations = source.observations || '';

      const service = source.service || {};
      this.form.service.nationalTaxationCode = service.nationalTaxationCode || '';
      this.form.service.municipalCode = service.municipalCode || '';
      this.form.service.nbsCode = service.nbsCode || '';
      this.form.service.description = service.description || '';
      this.form.service.issWithheldAtSource = service.issWithheldAtSource || false;
      this.form.service.issRate = service.issRate || 0;
      // IBS/CBS: se a nota de origem não trouxer, cai no config/fallback de saúde
      // (mesmo comportamento de uma nota nova).
      const cfg = (this.settings && this.settings.service) || {};
      this.form.service.cbsRate = service.cbsRate || cfg.cbsRate || 0;
      this.form.service.ibsRate = service.ibsRate || cfg.ibsRate || 0;
      this.form.service.ibsCbsReduction = service.ibsCbsReduction || cfg.ibsCbsReduction || 0;

      // Retenções são persistidas em valor (não alíquota): reconstrói o percentual.
      const total = source.totalAmount || 0;
      const toRate = amount => (total ? round((amount || 0) / total * 100, 2) : 0);
      this.form.service.irRate = toRate(service.irAmount);
      this.form.service.pisRate = toRate(service.pisAmount);
      this.form.service.cofinsRate = toRate(service.cofinsAmount);
      this.form.service.csllRate = toRate(service.csllAmount);
      this.form.service.inssRate = toRate(service.inssAmount);

      // Preserva o vínculo com a origem (transação/conta) ao reemitir.
      const origin = source.origin || {};
      this.sourceOrigin = buildOrigin(origin.type, origin.id);
    },
    setSourceEntity(entity) {
      if (!entity) return;

      this.form.entity.id = entity.id || null;
      this.form.entity.personType = entity.personType || 'person';
      this.form.entity.name = entity.name || '';
      this.form.entity.email = entity.email || '';
      this.form.entity.phone = entity.phone || '';

      if (entity.identity && this.identityTypes.includes(entity.identity.type)) {
        this.form.entity.identity = {
          type: entity.identity.type,
          value: entity.identity.value,
        };
      }

      const address = entity.address || {};
      this.form.entity.address = {
        postalCode: address.postalCode || '',
        street: address.street || '',
        number: address.number || '',
        complement: address.complement || '',
        neighborhood: address.neighborhood || '',
        city: address.city || '',
        state: address.state || '',
      };

      this.entityAc = {
        id: this.form.entity.id,
        name: this.form.entity.name,
      };
    },
    setWatchers() {
      const percentFields = [
        'iss',
        'ir',
        'pis',
        'cofins',
        'csll',
        'inss',
        'cbs',
        'ibs',
      ];

      percentFields.forEach((field) => {
        const fieldRate = `${field}Rate`;
        const fieldAmount = `${field}Amount`;

        this.form.service[fieldRate] = this[fieldRate];

        this.$watch(
          () => {
            // O ISS incide sobre a base líquida (valor - deduções); os demais,
            // sobre o valor total. Reage a mudanças de alíquota, total e deduções.
            const base = field === 'iss'
              ? Math.max(0, this.form.totalAmount - (this.form.deductions || 0))
              : this.form.totalAmount;
            return round(base * this.form.service[fieldRate] / 100, 2);
          },
          (value) => {
            this.form.service[fieldAmount] = value;
          },
          { immediate: true },
        );
      });
    },
    async save() {
      if (!this.canCreate) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }

      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return;
      }

      this.saving = true;
      this.statusReason = null;
      try {
        const { data } = await this.$http.post('/fiscal-documents', this.preparePayload());

        // A emissão passou a ser assíncrona: a nota nasce "pendente" e é
        // processada em segundo plano. Abre o detalhe, que acompanha o status
        // (emitida / rejeitada / falha) por polling e permite tentar novamente.
        this.$toast.success('Nota fiscal enviada para emissão');
        this.$emit('created', data.id);
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    preparePayload() {
      return {
        entity: {
          ...this.form.entity,
          identity: {
            type: this.form.entity.identity.type,
            value: clearNonNumbers(this.form.entity.identity.value),
          },
          address: {
            ...this.form.entity.address,
            postalCode: clearNonNumbers(this.form.entity.address.postalCode),
          },
          phone: clearNonNumbers(this.form.entity.phone),
        },
        service: { ...this.form.service },
        totalAmount: this.form.totalAmount,
        deductions: this.form.deductions,
        observations: this.form.observations,
        origin: this.origin || this.sourceOrigin,
      };
    },
    async findEntity(search) {
      const params = {
        customerId: this.branch.customerId,
        search: /^\d/.test(search) ? search : `^${search}`,
      };

      try {
        const { data } = await this.$http.get('/entities', { params });
        if (data.items.length > 0) {
          return data.items.map((item) => {
            const summaryParts = [item.type === 'person' ? 'PF' : 'PJ'];
            if (item.identity && item.identity.value) {
              summaryParts.push((
                item.type === 'person' ? cpf(item.identity.value) : cnpj(item.identity.value)
              ));
            }
            if (item.email) {
              summaryParts.push(item.email);
            }

            item.summary = summaryParts.join(' - ');
            return item;
          });
        }
      } catch (e) {
        this.$toast.error(e);
      }

      return [{
        id: null,
        name: search,
        summary: 'Não cadastrado',
      }];
    },
    async findEntityById(id) {
      try {
        const { data } = await this.$http.get(`/entities/${id}`);
        return data;
      } catch (e) {
        this.$toast.error(e);
      }
      return null;
    },
    setEntity(data) {
      if (data) {
        this.form.entity.id = data.id || null;
        this.form.entity.name = data.name;
        this.form.entity.email = data.email || '';
        this.form.entity.phone = data.cellphone || data.phone || '';

        if (data.identity && this.identityTypes.includes(data.identity.type)) {
          this.form.entity.identity = data.identity;
        } else {
          this.form.entity.identity = this.blankForm().entity.identity;
        }

        const address = first(data.addresses);
        if (address) {
          const {
            street,
            number,
          } = extractStreetAndNumber(address.addressLine1);
          this.form.entity.address.postalCode = address.postalCode;
          this.form.entity.address.street = street;
          this.form.entity.address.number = number;
          this.form.entity.address.complement = address.addressLine2;
          this.form.entity.address.neighborhood = address.neighborhood;
          this.form.entity.address.city = address.city;
          this.form.entity.address.state = address.state;
        }

        if (this.entity && this.entity.name) {
          this.form.entity.name = this.entity.name;
          this.entityAc = {
            id: this.entity.id || null,
            name: this.entity.name,
          };
        }
      } else {
        this.form.entity = this.blankForm().entity;
      }
    },
    unsetEntity() {
      this.setEntity(null);
      this.entityAc = null;
    },
    async loadAddressByPostalCode() {
      const postalCode = clearNonNumbers(this.form.entity.address.postalCode, '');

      if (postalCode.length === 8 && !this.loadingAddress) {
        this.loadingAddress = true;
        this.postCodeValidated = false;

        try {
          const data = await this.$cep.get(postalCode);
          this.form.entity.address.city = data.city;
          this.form.entity.address.state = data.state;
          this.form.entity.address.neighborhood = data.neighborhood;
          this.form.entity.address.street = data.street;
        } catch (e) {
          this.$toast.error(e);
        }

        this.loadingAddress = false;
      }
    },
    close() {
      this.$emit('close');
    },
    blankForm() {
      return {
        entity: {
          id: null,
          personType: 'person',
          name: '',
          identity: {
            type: CPF,
            value: '',
          },
          email: '',
          phone: '',
          address: {
            postalCode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
          },
        },
        service: {
          nationalTaxationCode: '',
          municipalCode: '',
          nbsCode: '',
          description: '',
          issWithheldAtSource: false,
          issRate: 0,
          issAmount: 0,
          cofinsRate: 0,
          cofinsAmount: 0,
          csllRate: 0,
          csllAmount: 0,
          inssRate: 0,
          inssAmount: 0,
          irRate: 0,
          irAmount: 0,
          pisRate: 0,
          pisAmount: 0,
          cbsRate: 0,
          cbsAmount: 0,
          ibsRate: 0,
          ibsAmount: 0,
          ibsCbsReduction: 0,
        },
        totalAmount: 0,
        deductions: 0,
        observations: '',
      };
    },
  },
};
</script>
